import React, { useEffect, useState } from "react";

/**
 * Price — Page de retour post-paiement Stripe pour une course.
 *
 * Appelée par Stripe Checkout dans 2 contextes différents :
 *
 *  1) **Capacitor** (iOS SFSafariView / Android Chrome Custom Tabs) :
 *     Le navigateur in-app a été ouvert PAR L'APP. `window.close()`
 *     referme la fenêtre → Capacitor Browser plugin détecte
 *     `browserFinished` → le dashboard driver/passager poll l'état.
 *     Rien d'autre à faire côté web.
 *
 *  2) **Navigateur web standalone** (Safari/Chrome du téléphone) :
 *     L'utilisateur a cliqué sur un lien ou payé depuis pricecovoit.fr
 *     dans un onglet "normal". `window.close()` ne fonctionne pas
 *     (sécurité navigateur, l'onglet n'a pas été ouvert par JS).
 *     Dans ce cas on tente d'OUVRIR L'APP native via le custom URL
 *     scheme `com.price.covoiturage://` (défini dans Info.plist iOS +
 *     AndroidManifest.xml). Si l'app est installée, elle s'ouvre
 *     directement sur l'écran de suivi de course. Sinon, après 1,8s
 *     sans navigation, on redirige vers /download pour inviter à
 *     installer l'app.
 */
export default function PricePaymentReturn() {
  const [stage, setStage] = useState("closing");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("ride_paid") === "1";
    const cancelled = params.get("ride_cancelled") === "1";
    const requestId = params.get("request_id") || "";
    const sessionId = params.get("session_id") || "";

    // Tentative 1 — Capacitor in-app browser : refermer la fenêtre.
    // Si on est en WebView native, window.close() réussit et
    // l'utilisateur revient immédiatement dans l'app.
    try { window.close(); } catch (_e) { /* ignore */ }

    // Tentative 2 — Navigateur web : tenter d'ouvrir l'app via deep
    // link custom scheme. iOS/Android reconnaîtra l'URL si l'app
    // est installée et basculera dessus → la page reste en arrière-
    // plan. Si l'app n'est pas installée, le navigateur ignore le
    // scheme (ou affiche une alerte brève) et notre fallback /download
    // s'affiche après 1,8s.
    const deepLinkPath = paid
      ? `payment-return?ride_paid=1&request_id=${requestId}&session_id=${sessionId}`
      : cancelled
      ? `payment-return?ride_cancelled=1&request_id=${requestId}`
      : "payment-return";
    const deepLink = `com.price.covoiturage://${deepLinkPath}`;
    // iframe hack = ne quitte pas la page si l'app n'est pas installée,
    // contrairement à window.location.href = deepLink qui peut casser
    // le fallback timer. Si l'app est installée, iOS/Android récupère
    // le scheme et bascule hors du navigateur.
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = deepLink;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 500);
    } catch (_e) { /* ignore */ }

    // Tentative 3 (fallback 1,8s) — l'utilisateur n'a probablement pas
    // l'app installée (ou le scheme a été bloqué). On l'envoie sur la
    // page `/download` qui met en avant les stores + les réseaux
    // sociaux. On préserve le résultat du paiement dans l'URL pour que
    // la page download puisse afficher un petit toast "paiement réussi,
    // ouvre l'app installée !".
    const t = setTimeout(() => {
      setStage("redirecting");
      const target = paid
        ? `/download?paid=1&request_id=${requestId}`
        : cancelled
        ? `/download?cancelled=1&request_id=${requestId}`
        : "/download";
      window.location.replace(target);
    }, 1800);

    return () => clearTimeout(t);
  }, []);

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const paid = params.get("ride_paid") === "1";

  return (
    <div
      data-testid="price-payment-return"
      style={{
        minHeight: "100dvh",
        background: "#FFF8F1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "Outfit, system-ui, sans-serif",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 24,
          border: "2px solid #1E0910",
          boxShadow: "6px 6px 0 0 #1E0910",
          padding: 28,
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 16px",
            borderRadius: 18,
            background: paid ? "#D6F5D6" : "#FFEEDD",
            border: "2px solid #1E0910",
            display: "grid",
            placeItems: "center",
            fontSize: 36,
          }}
        >
          {paid ? "✅" : "💳"}
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 8px",
            color: "#1E0910",
          }}
        >
          {paid ? "Paiement reçu !" : "Paiement en cours…"}
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "rgba(30,9,16,0.65)",
            margin: 0,
            fontFamily: "Manrope, system-ui, sans-serif",
          }}
        >
          {stage === "closing"
            ? "Retour à l'application Price en cours…"
            : "Ouverture de l'app en cours… Si rien ne se passe, télécharge Price pour suivre ta course."}
        </p>
      </div>
    </div>
  );
}
